/**
 * 宠物篮球：规则核心（B，pet_arcade/core；合同 docs/coordination/PET-ARCADE-BASKET-RULES-B-2026-10-01.md）。
 *
 * 单人练习短局：左右移动 → 按住蓄力（力度来回摆）→ 松手起跳出手 → 球按真实抛物线飞 → 过篮筐那一步判进没进 → 落地、等球回来 → 再投。
 * 侧视场地，逻辑单位 360×640，y 向下。球的位置每一步都在状态里，进没进由球穿过篮筐平面那一步算出来——画面上的球与得分永远一致。
 * 纯 TypeScript：不碰 DOM、计时器、Math.random；固定步进（loop.ts）＋种子随机（只用于擦框进不进）。局内成绩只留在这个对象里。
 */

import { createFixedStepClock, STEP_MS } from "./loop";
import { createEventStamper, type Stamped } from "./events";
import { createRng, type Rng } from "./rng";

export interface BasketInput { move: number; charge: boolean } // move：-1～1（左右）；charge：按住＝蓄力，松开＝出手

export const FLOOR_Y = 600;
export const HOOP_X = 320;
export const RIM_Y = 450;
export const RIM_HALF = 24; // 篮筐半宽
export const BALL_R = 10;
export const SWISH_HALF = 6; // 球心离筐心这么近＝空心
export const RELEASE_UP = 90; // 出手点在脚下多高
export const PET_MIN_X = 20;
export const PET_MAX_X = 220; // 离筐至少 100：再近就是扣篮，首版不做
export const THREE_POINT_DISTANCE = 220; // 离筐（水平）≥ 这个数算三分
export const MOVE_SPEED = 160; // 每秒单位
export const GRAVITY = 900; // 每秒² 单位
export const LAUNCH_ANGLE = (65 * Math.PI) / 180; // 固定出手角：远投有高弧线；近投（100）顶点也比筐高十来个单位
export const V_MIN = 340; // 最轻的球够不到筐的高度（空气球偏近）
export const V_MAX = 660; // 力度 0～1 线性对应出手速度；刚好的力度约 0.2（离筐 100）～0.88（离筐 300）
export const CHARGE_PERIOD_MS = 1400; // 力度 0→1→0 一个来回
export const JUMP_MS = 450;
export const RETURN_AFTER_MADE_MS = 800;
export const RETURN_AFTER_MISS_MS = 1000;
export const RIM_IN_CHANCE = 0.4; // 擦到筐沿时滚进去的机会（种子决定）
export const BASKET_POINTS = { two: 2, three: 3, swish: 1, hot: 1 } as const; // 连中第 3 个起每个 +hot

export type BasketPhase = "ready" | "running" | "paused" | "ended";
export type BasketStance = "holding" | "charging" | "airborne" | "waiting";
export type BasketEndReason = "time_up" | "quit";
export type BasketMissReason = "short" | "long" | "rim_out";

export interface BasketConfig { durationMs: number; seed: number; startX: number }
export const DEFAULT_BASKET_CONFIG: BasketConfig = { durationMs: 60_000, seed: 1, startX: 120 };

export interface BasketResult {
  reason: BasketEndReason; counted: boolean; score: number; durationMs: number;
  shots: number; makes: number; swishes: number; threes: number; bestStreak: number;
}

export interface BasketState {
  phase: BasketPhase; seed: number; elapsedMs: number; remainingMs: number; score: number; streak: number;
  pet: { x: number; stance: BasketStance; airborneMs: number };
  power: number | null; // 蓄力时 0～1，否则 null
  ball: { x: number; y: number; state: "held" | "flying" | "resting"; shotId: number | null };
  hoop: { x: number; rimY: number; rimHalf: number }; threePointDistance: number; floorY: number;
  result: BasketResult | null;
}

export type BasketEventBody =
  | { type: "started"; seed: number; durationMs: number }
  | { type: "charge_started" }
  | { type: "shot"; shotId: number; power: number; distance: number; value: 2 | 3 }
  | { type: "rim"; shotId: number }
  | { type: "scored"; shotId: number; points: number; value: 2 | 3; swish: boolean; rimIn: boolean; streak: number }
  | { type: "missed"; shotId: number; reason: BasketMissReason }
  | { type: "landed" }
  | { type: "ball_returned" }
  | { type: "buzzer"; shotId: number } // 时间到时球还在飞：等它落定再结束，照样算
  | { type: "paused"; elapsedMs: number }
  | { type: "resumed"; elapsedMs: number }
  | { type: "ended"; reason: BasketEndReason; counted: boolean; result: BasketResult };

/** 对外的规则事件：事件体＋唯一标识 (round, step, seq)（动作清单 F0 修订 R2，见 events.ts）。 */
export type BasketEvent = Stamped<BasketEventBody>;

export interface BasketGame {
  readonly state: BasketState;
  setInput(input: BasketInput): void;
  advance(frameMs: number): BasketEvent[];
  start(): BasketEvent[];
  pause(): BasketEvent[];
  resume(): BasketEvent[];
  quit(): BasketEvent[];
  restart(seed?: number): BasketEvent[];
}

/** 这个距离上刚好穿过筐心要的出手速度（抛物线反解）；给用例与调参用，UI 不要拿它当提示。 */
export function idealSpeed(distance: number): number {
  const rise = FLOOR_Y - RELEASE_UP - RIM_Y; // 出手点到筐的高度差（向上为正）
  const cos = Math.cos(LAUNCH_ANGLE);
  return Math.sqrt((GRAVITY * distance * distance) / (2 * cos * cos * (distance * Math.tan(LAUNCH_ANGLE) - rise)));
}

export function idealPower(distance: number): number {
  return (idealSpeed(distance) - V_MIN) / (V_MAX - V_MIN);
}

/** 力度摆：按住后 t 毫秒的力度（0→1→0 来回）。 */
export function powerAt(chargedMs: number): number {
  const phase = (chargedMs % CHARGE_PERIOD_MS) / (CHARGE_PERIOD_MS / 2);
  return phase <= 1 ? phase : 2 - phase;
}

export function createBasketGame(overrides: Partial<BasketConfig> = {}): BasketGame {
  const config: BasketConfig = { ...DEFAULT_BASKET_CONFIG, ...overrides };
  const clock = createFixedStepClock();
  const stamper = createEventStamper();
  const durationSteps = Math.round(config.durationMs / STEP_MS);
  const dt = STEP_MS / 1000;
  let rng: Rng;
  let seed = config.seed;
  let phase: BasketPhase;
  let steps: number;
  let score: number;
  let streak: number;
  let petX: number;
  let stance: BasketStance;
  let airborneMs: number;
  let chargeSteps: number;
  let ball: { x: number; y: number; vx: number; vy: number; state: "held" | "flying" | "resting"; shotId: number | null; value: 2 | 3; restMs: number;
    judged: boolean };
  let shotSerial: number;
  let input: BasketInput;
  let buzzerSent: boolean;
  let stats: { shots: number; makes: number; swishes: number; threes: number; bestStreak: number };
  let result: BasketResult | null;
  let snapshot: BasketState;

  function held(): typeof ball {
    return { x: petX, y: FLOOR_Y - RELEASE_UP, vx: 0, vy: 0, state: "held", shotId: null, value: 2, restMs: 0, judged: false };
  }

  function build(): BasketState {
    const elapsedMs = steps * STEP_MS;
    return {
      phase, seed, elapsedMs, remainingMs: Math.max(0, config.durationMs - elapsedMs), score, streak,
      pet: { x: petX, stance, airborneMs }, power: stance === "charging" ? powerAt(chargeSteps * STEP_MS) : null,
      ball: { x: ball.x, y: ball.y, state: ball.state, shotId: ball.shotId },
      hoop: { x: HOOP_X, rimY: RIM_Y, rimHalf: RIM_HALF }, threePointDistance: THREE_POINT_DISTANCE, floorY: FLOOR_Y, result,
    };
  }

  function reset(newSeed: number): void {
    seed = newSeed;
    rng = createRng(newSeed);
    clock.reset();
    phase = "ready";
    steps = 0;
    score = 0;
    streak = 0;
    petX = Math.max(PET_MIN_X, Math.min(PET_MAX_X, config.startX));
    stance = "holding";
    airborneMs = 0;
    chargeSteps = 0;
    ball = held();
    shotSerial = 0;
    input = { move: 0, charge: false };
    buzzerSent = false;
    stats = { shots: 0, makes: 0, swishes: 0, threes: 0, bestStreak: 0 };
    result = null;
    snapshot = build();
  }

  function finish(reason: BasketEndReason, events: BasketEventBody[]): void {
    if (stance === "airborne") { // 结束时 TA 还在空中（弱球落地前一帧判完正好到点、空中退出）：先补发落地再结束，事件序列闭合（F0 修订 G2）
      stance = "waiting";
      airborneMs = 0;
      events.push({ type: "landed" });
    }
    phase = "ended";
    result = { reason, counted: reason !== "quit", score, durationMs: Math.min(steps * STEP_MS, config.durationMs), ...stats };
    events.push({ type: "ended", reason, counted: result.counted, result });
  }

  function release(events: BasketEventBody[]): void {
    const power = powerAt(chargeSteps * STEP_MS);
    const speed = V_MIN + power * (V_MAX - V_MIN);
    const distance = HOOP_X - petX;
    const value: 2 | 3 = distance >= THREE_POINT_DISTANCE ? 3 : 2;
    shotSerial += 1;
    stats.shots += 1;
    ball = { x: petX, y: FLOOR_Y - RELEASE_UP, vx: speed * Math.cos(LAUNCH_ANGLE), vy: -speed * Math.sin(LAUNCH_ANGLE), state: "flying", shotId: shotSerial,
      value, restMs: 0, judged: false };
    stance = "airborne";
    airborneMs = JUMP_MS;
    chargeSteps = 0;
    events.push({ type: "shot", shotId: shotSerial, power, distance, value });
  }

  function judge(xCross: number, events: BasketEventBody[]): void {
    const off = Math.abs(xCross - HOOP_X);
    const id = ball.shotId as number;
    let made = false;
    let rimIn = false;
    let reason: BasketMissReason = xCross < HOOP_X ? "short" : "long";
    if (off <= RIM_HALF - BALL_R) made = true;
    else if (off <= RIM_HALF + BALL_R) {
      events.push({ type: "rim", shotId: id });
      rimIn = rng.next() < RIM_IN_CHANCE;
      made = rimIn;
      reason = "rim_out";
    }
    ball.judged = true;
    if (made) {
      const swish = off <= SWISH_HALF;
      streak += 1;
      const points = (ball.value === 3 ? BASKET_POINTS.three : BASKET_POINTS.two) + (swish ? BASKET_POINTS.swish : 0) + (streak >= 3 ? BASKET_POINTS.hot : 0);
      score += points;
      stats.makes += 1;
      stats.swishes += swish ? 1 : 0;
      stats.threes += ball.value === 3 ? 1 : 0;
      stats.bestStreak = Math.max(stats.bestStreak, streak);
      events.push({ type: "scored", shotId: id, points, value: ball.value, swish, rimIn, streak });
      ball = { ...ball, x: HOOP_X, vx: 0, vy: Math.max(ball.vy, 60) }; // 从筐里落下
      ball.restMs = RETURN_AFTER_MADE_MS;
    } else {
      streak = 0;
      events.push({ type: "missed", shotId: id, reason });
      if (reason === "rim_out") ball.vx = -Math.abs(ball.vx) * 0.5; // 从筐沿弹回来
      ball.restMs = RETURN_AFTER_MISS_MS;
    }
  }

  function stepBall(events: BasketEventBody[]): void {
    if (ball.state === "held") {
      ball.x = petX;
      return;
    }
    if (ball.state === "flying") {
      const x0 = ball.x;
      const y0 = ball.y;
      ball.vy += GRAVITY * dt;
      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;
      if (!ball.judged && ball.vy > 0 && y0 < RIM_Y && ball.y >= RIM_Y) { // 下落中穿过篮筐平面的那一步
        const t = (RIM_Y - y0) / (ball.y - y0);
        judge(x0 + (ball.x - x0) * t, events);
      }
      if (ball.y >= FLOOR_Y - BALL_R) {
        ball.y = FLOOR_Y - BALL_R;
        if (!ball.judged) { // 没够到筐的高度就落地：偏近
          streak = 0;
          ball.judged = true;
          events.push({ type: "missed", shotId: ball.shotId as number, reason: "short" });
          ball.restMs = RETURN_AFTER_MISS_MS;
        }
        ball.state = "resting";
        ball.vx = 0;
        ball.vy = 0;
      }
      return;
    }
    ball.restMs = Math.max(0, ball.restMs - STEP_MS); // resting：等一会儿滚回来
    if (ball.restMs === 0 && stance === "waiting" && phase === "running") {
      ball = held();
      stance = "holding";
      events.push({ type: "ball_returned" });
    }
  }

  function step(events: BasketEventBody[]): void {
    steps += 1;
    const timeUp = steps >= durationSteps;
    if (stance === "holding") {
      const move = Math.max(-1, Math.min(1, Number.isFinite(input.move) ? input.move : 0));
      petX = Math.max(PET_MIN_X, Math.min(PET_MAX_X, petX + move * MOVE_SPEED * dt));
      if (input.charge && !timeUp) {
        stance = "charging";
        chargeSteps = 0;
        events.push({ type: "charge_started" });
      }
    } else if (stance === "charging") {
      chargeSteps += 1;
      if (!input.charge) release(events);
    } else if (stance === "airborne") {
      airborneMs = Math.max(0, airborneMs - STEP_MS);
      if (airborneMs === 0) {
        stance = "waiting";
        events.push({ type: "landed" });
      }
    }
    // 落地之前球就回不来：等待态由 stepBall 在球回来时改回 holding
    stepBall(events);
    if (timeUp) {
      if (ball.state === "flying" && !ball.judged) {
        if (!buzzerSent) {
          buzzerSent = true;
          events.push({ type: "buzzer", shotId: ball.shotId as number });
        }
        return; // 压哨球：等它判完再结束
      }
      finish("time_up", events);
    }
  }

  reset(seed);

  return {
    get state() {
      return snapshot;
    },
    setInput(next: BasketInput): void {
      input = next;
    },
    start(): BasketEvent[] {
      if (phase !== "ready") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<BasketEventBody>([{ type: "started", seed, durationMs: config.durationMs }], steps);
    },
    advance(frameMs: number): BasketEvent[] {
      if (phase !== "running") return [];
      const stamped: BasketEvent[] = [];
      const n = clock.feed(frameMs);
      for (let i = 0; i < n && phase === "running"; i++) {
        const body: BasketEventBody[] = [];
        step(body);
        stamped.push(...stamper.stamp(body, steps));
      }
      snapshot = build();
      return stamped;
    },
    pause(): BasketEvent[] {
      if (phase !== "running") return [];
      phase = "paused";
      clock.reset();
      snapshot = build();
      return stamper.stamp<BasketEventBody>([{ type: "paused", elapsedMs: steps * STEP_MS }], steps);
    },
    resume(): BasketEvent[] {
      if (phase !== "paused") return [];
      phase = "running";
      snapshot = build();
      return stamper.stamp<BasketEventBody>([{ type: "resumed", elapsedMs: steps * STEP_MS }], steps);
    },
    quit(): BasketEvent[] {
      if (phase !== "running" && phase !== "paused") return [];
      const events: BasketEventBody[] = [];
      finish("quit", events);
      snapshot = build();
      return stamper.stamp(events, steps);
    },
    restart(newSeed?: number): BasketEvent[] {
      reset(newSeed ?? seed);
      stamper.nextRound();
      phase = "running";
      snapshot = build();
      return stamper.stamp<BasketEventBody>([{ type: "started", seed, durationMs: config.durationMs }], steps);
    },
  };
}
